// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';

import { type LookAndFeelRootPropsCanceledPayment } from '@tectonics/look-and-feel';
import { IncomingPaymentProvider, useIncomingPayment, getCountry, getRedirectUrlAfterPayment } from '@tectonics/prs/incoming-payment';
import { PaymentSpecProvider } from '@tectonics/prs/payment-spec';
import { UserInputValuesProvider } from '@tectonics/prs/user-input-values';

import { GlobalStyle } from '../../commons/global-style';
import {
  LayoutBase,
  LayoutSideLeft,
  LayoutSideRight,
  MainPanel,
  MainPanelContent,
} from '../../template';
import { SideDisplay } from '../../organisms/side-display';
import { MerchantInfo } from '../../organisms/merchant-info';

export function PageCanceledPayment({
  spec,
  incomingPayment,
  userInputValues,
  onUserInput,
}: LookAndFeelRootPropsCanceledPayment): Node {
  return (
    <PaymentSpecProvider spec={spec}>
      <IncomingPaymentProvider incomingPayment={incomingPayment}>
        <UserInputValuesProvider values={userInputValues} setValue={onUserInput}>

          <GlobalStyle />

          <LayoutBase data-test-id="page-canceled-payment">
            <LayoutSideLeft>
              <MainPanel>
                <MainPanelContent>
                  <Layout />
                </MainPanelContent>
              </MainPanel>
            </LayoutSideLeft>

            <LayoutSideRight>
              <SideDisplay />
            </LayoutSideRight>
          </LayoutBase>
        </UserInputValuesProvider>
      </IncomingPaymentProvider>
    </PaymentSpecProvider>
  );
}

function Layout(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  const redirectUrl = getRedirectUrlAfterPayment(incomingPayment);

  return (
    <Content>
      <CanceledMessage country={country} />
      {redirectUrl && <MerchantInfo />}
    </Content>
  );
}

type messageProps = {|
  country: string,
|};

function CanceledMessage({ country }: messageProps): Node {
  switch (country) {
    case 'br':
      return (
        <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Header>Este pagamento foi cancelado</Header>
          <Body>
            <p>
              Se você quiser saber mais detalhes sobre esse pagamento,
              acesse o site onde a compra foi feita.
            </p>
          </Body>
        </div>
      );

    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
    case 'cr':
    case 'py':
    case 'pa':
    case 'sv':
    case 'gt':
    case 'do':
      return (
        <>
          <Header>Este pago fue cancelado</Header>
          <Body>
            <p>
              Si desea conocer más detalles sobre este pago,
              acceda al sitio donde se realizó la compra.
            </p>
          </Body>
        </>
      );

    case 'za':
    case 'ke':
    case 'ng':
    case 'in':
    case 'tz':
    case 'ug':
    case 'zm':
    case 'eg':
    case 'gh':
    case 'jm':
      return (
        <>
          <Header>This payment has been cancelled</Header>
          <Body>
            <p>
              If you want to know more details about this payment,
              go to the website where the purchase was made.
            </p>
          </Body>
        </>
      );

    default:
      throw new Error(`CanceledMessage: country not supported "${country}"`);
  }
}

const Header = styled.h1`
  font-weight: 200;
  font-size: 1.8em;
  line-height: 1.5;
  color: ${props => props.theme.colors.primary(39)};
`;

const Body = styled.div`
  line-height: 1.5;
`;

const Content = styled.section`
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-content: center;

  text-align: center;
  font-family: ${props => props.theme.fontFamily.display};

  ${Header}, ${Body} {
    padding: .4em 1em;
  }

  @media (min-width: 840px) {
    height: 60vh;

    & > * {
      &:last-child {
        border-radius: 0 0 7px 7px;
      }
    }
  }
`;
